/**
 * learningMemory.js — MOAT 1.0 Phase 8 · structured child learning memory.
 * Educational tags only (no conversation data, no PII). Two sources:
 *   1. derived — recomputed from real evidence every read (never stored)
 *   2. noted   — tags accepted from the AI orchestrator or a grown-up (stored, capped, removable)
 * Memory influences recommendations; it never determines mastery.
 */
import { getActiveChildId } from "@/lib/children";
import { REWARDS } from "@/lib/canonical";
import { worldStats } from "@/lib/ttqs";
import { loadMastery } from "@/lib/mastery";
import { getWeakSkills } from "@/lib/skillLedger";
import { loadProgress } from "@/lib/progress";
import { recentGames } from "@/lib/gameCatalog";

export const TAG_TEXT = {
  "needs:ttqs-retrieval": "Needs more retrieval for multiplication facts.",
  "strong:ttqs": "Quick and accurate with times tables.",
  "prefers:visual": "Strong with visual explanations.",
  "prefers:games": "Enjoys learning through games.",
  "prefers:world": "Enjoys geography and world challenges.",
  "ready:stretch": "Ready for stretch problems.",
  "benefits:worked-examples": "Benefits from worked examples.",
  "reads:comfortably": "Reads comfortably at current level.",
  "struggles:fractions": "Struggles with equivalent fractions.",
};
const key = () => `${REWARDS.memoryKey}:${getActiveChildId() || "default"}`;
export function notedTags() { try { return JSON.parse(localStorage.getItem(key()) || "[]") || []; } catch { return []; } }
export function noteTag(tag, source = "grown-up") {
  if (!tag || typeof tag !== "string" || tag.length > 40) return notedTags();
  const list = notedTags().filter((t) => t.tag !== tag);
  list.unshift({ tag, source, at: Date.now() });
  try { localStorage.setItem(key(), JSON.stringify(list.slice(0, 12))); } catch { /* ignore */ }
  return list;
}
export function forgetTag(tag) { try { localStorage.setItem(key(), JSON.stringify(notedTags().filter((t) => t.tag !== tag))); } catch { /* ignore */ } }

/** Deterministic tags from evidence. Cautious thresholds; nothing invented. */
export function derivedTags() {
  const tags = [];
  try {
    const w = worldStats();
    if ((w.weak || 0) >= 3) tags.push("needs:ttqs-retrieval");
    if ((w.quick || 0) >= 20 && (w.weak || 0) <= 1) tags.push("strong:ttqs");
  } catch { /* ignore */ }
  const m = loadMastery();
  const good = Object.values(m).filter((r) => r.mastered).length, tried = Object.values(m).filter((r) => r.attempts?.length).length;
  if (tried >= 6 && good / tried >= 0.75) tags.push("ready:stretch");
  if (getWeakSkills({ limit: 3 }).length >= 2) tags.push("benefits:worked-examples");
  if (Object.keys(m).some((c) => /frac/i.test(c) && !m[c].mastered && (m[c].attempts || []).length >= 2)) tags.push("struggles:fractions");
  const p = loadProgress();
  if ((p.countriesLearned || []).length >= 5) tags.push("prefers:world");
  if (recentGames(5).length >= 4) tags.push("prefers:games");
  return tags;
}
export function memoryTags() { return [...new Set([...derivedTags(), ...notedTags().map((t) => t.tag)])]; }
export const memoryLines = () => memoryTags().map((t) => TAG_TEXT[t] || t.replace(/[:-]/g, " "));
